import { action } from '@ember/object'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import type StudentCsvMaker from 'teacher-ui-client/services/student-csv-maker'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'
import { optional } from 'teacher-ui-client/helpers/optional'
import { didCancel } from 'ember-concurrency'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'

interface Args {
  oppositeSubscriptionType: SubscriptionType
}

export class RolloverFinishStep extends Component<Args> {
  @service studentCsvMaker!: StudentCsvMaker

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  get isDownloadCsvEnabled() {
    return this.studentCsvMaker.downloadRolloverCsv.isIdle
  }

  @action
  async downloadRolloverCsv() {
    const filename = this.intl.t('manage.rollover.steps.students.export.filename')
    try {
      const result = await this.studentCsvMaker.downloadRolloverCsv.perform(filename)
      if (result.isOk()) {
        this.showSuccessMessage(result.value.studentCount)
      } else {
        this.showFailMessage()
      }
    } catch (error: unknown) {
      if (!didCancel(error)) {
        this.showFailMessage()
      }
    }
  }

  private showSuccessMessage(studentCount: number) {
    const successMessage = this.intl.t('manage.manageClasses.students.bulkActions.exportStudents.success', {
      count: studentCount,
    })
    this.flashQueue.addSuccess({ subtitle: successMessage })
  }

  private showFailMessage() {
    const errorMessage = this.intl.t('manage.manageClasses.students.bulkActions.exportStudents.error')
    this.flashQueue.addFail({ subtitle: errorMessage })
  }

  <template>
    <div class="mx-auto max-w-3xl px-2">
      <div class="mb-12">
        <h3 class="text-dusty-black-250 mt-8 mb-6">
          {{t "manage.rollover.steps.finish.stepContentHeader"}}
        </h3>
        <InlineAlert class="mb-6" @type={{AlertType.Information}}>
          <InlineAlertSubtitle>
            <div>{{t
                "manage.rollover.steps.finish.body.oppositeRollover"
                subscriptionType=(t @oppositeSubscriptionType)
              }}</div>
            <span
              class="{{if this.isDownloadCsvEnabled 'cursor-pointer' 'cursor-wait'}}
                text-black hover:text-black focus:text-black"
              role="button"
              data-test-download-students-csv
              {{on "click" (optional (if this.isDownloadCsvEnabled this.downloadRolloverCsv))}}
            >
              <span class="font-bold underline">{{t "manage.rollover.steps.finish.body.export1"}}</span>
              <span>{{t "manage.rollover.steps.finish.body.export2"}}</span>
            </span>
          </InlineAlertSubtitle>
        </InlineAlert>
        <div class="space-y-6 text-xl">
          <p>{{t "manage.rollover.steps.finish.body.cta" htmlSafe=true}}</p>
          <p>
            {{t "manage.rollover.steps.finish.body.effectPre"}}
            <ul class="pt-2 pl-12">
              <li>
                -
                {{t "manage.rollover.steps.finish.body.effect1" subscriptionType=(t @oppositeSubscriptionType)}}
              </li>
              <li>
                -
                {{t "manage.rollover.steps.finish.body.effect2"}}
              </li>
            </ul>
          </p>
          <p>
            {{t "manage.rollover.steps.finish.body.signOff"}}
          </p>
        </div>
      </div>
    </div>
  </template>
}

export default RolloverFinishStep
